import React, { useState, useEffect, useRef } from 'react';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import CloseIcon from '@mui/icons-material/Close';
import './chat.css';

interface Chat {
  id: string;
  sender: string;
  content: string;
  timestamp: Date;
  userId: string;
  file?: string;
  itemId?: string; // ✅ הוספת taskId לממשק
}

interface ChatTableProps {
  itemId?: string;
}

const ChatTable: React.FC<ChatTableProps> = ({ itemId }) => {
  const [messages, setMessages] = useState<Chat[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const apiUrl = process.env.REACT_APP_BRAVERMAN;
  const currentSender = sessionStorage.getItem('userType') || 'User';
  const userId = sessionStorage.getItem('userId') || 'defaultUserId';

  useEffect(() => {
    const fetchMessages = async () => {
      if (!itemId) return;
      try {
        const res = await fetch(`${apiUrl}Chat/task/${itemId}`);
        const data = await res.json();
        const parsed = data.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        }));
        setMessages(parsed);
      } catch (error) {
        console.error('Failed to fetch chat history:', error);
      }
    };

    fetchMessages();
  }, [itemId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!newMessage.trim() && !imagePreview) return;

    const fullMessage: Chat = {
      id: '',
      sender: currentSender,
      content: newMessage.trim(),
      timestamp: new Date(),
      userId,
      file: imagePreview || undefined,
      itemId, 
    };

    try {
      const res = await fetch(`${apiUrl}Chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fullMessage),
      });

      if (res.ok) {
        const newId = await res.text();
        setMessages((prev) => [...prev, { ...fullMessage, id: newId }]);
        setNewMessage('');
        setImagePreview(null);
      } else {
        const err = await res.text();
        console.error('Server error:', err);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => setImagePreview(null);

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('he-IL');
  };
const currentSender1 = sessionStorage.getItem('fullName') || 'User';

  return (
    <div className="chat-room">
      <div className="messages-box">
        {messages.map((msg, idx) => {
          const isCurrentUser = msg.sender === currentSender1;
          return (
            <div key={idx} className={`masg ${isCurrentUser ? 'sent' : 'received'}`}>
              <div className="msg-header">
                <div className="sender-info">
                  <AccountCircleOutlinedIcon className="sender-icon" />
                  <strong>{isCurrentUser ? 'את/ה' : msg.sender}</strong>
                </div>
                <span>{formatDate(msg.timestamp)}</span>
              </div>
              <div className="msg-content">
                {msg.content && <span>{msg.content}</span>}
                {msg.file && (
                  <img src={msg.file} alt="file" style={{ maxWidth: '100%' }} />
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="chat-input input-with-preview">
        <div className="input-wrapper">
          {imagePreview && (
            <div className="image-in-input">
              <img src={imagePreview} alt="preview" />
              <button onClick={handleRemoveImage} type="button">
                <CloseIcon fontSize="small" />
              </button>
            </div>
          )}
          <input
            type="text"
            placeholder="כתבי הודעה..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
          />
        </div>

        <input
          type="file"
          id="upload-image"
          style={{ display: 'none' }}
          accept="image/*"
          onChange={handleFileChange}
        />
        <label htmlFor="upload-image" title="העלאת קובץ">
          <AttachFileRoundedIcon className="inputIcon" />
        </label>
      </form>
    </div>
  );
};

export default ChatTable;
